import { useAppSelector } from '../../store';
import { selectDiagnosticReadiness } from '../../store/selectors';
import { DynamicCase } from '@/types';
import { Box, Button, TextField, Typography } from "@mui/material";
import { FileText } from "lucide-react";
import React, { useState } from "react";

interface SubmitContainerProps {
  onSubmitDiagnosis: (diagnosis: string) => void;
  currentCase: DynamicCase | null;
  fullHeight?: boolean;
  diagnosisInput?: string;
  onDiagnosisInputChange?: (value: string) => void;
}

export const DiagnosisNotes: React.FC<SubmitContainerProps> = ({
  onSubmitDiagnosis,
  currentCase: _currentCase,
  fullHeight = false,
  diagnosisInput: controlledDiagnosisInput,
  onDiagnosisInputChange,
}) => {
  const [localDiagnosisInput, setLocalDiagnosisInput] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const diagnosisInput = controlledDiagnosisInput ?? localDiagnosisInput;

  const diagnosticReadiness = useAppSelector(selectDiagnosticReadiness);

  const setDiagnosisInput = (value: string) => {
    if (onDiagnosisInputChange) {
      onDiagnosisInputChange(value);
      return;
    }

    setLocalDiagnosisInput(value);
  };

  const handleSubmitDiagnosis = (e: React.FormEvent) => {
    e.preventDefault();
    if (diagnosisInput.trim() && !isSubmitted && diagnosticReadiness.canSubmitDiagnosis) {
      onSubmitDiagnosis(diagnosisInput);
      setIsSubmitted(true);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (diagnosisInput.trim() && !isSubmitted && diagnosticReadiness.canSubmitDiagnosis) {
        handleSubmitDiagnosis(e);
      }
    }
  };

  // Determine button state and text
  const getButtonState = () => {
    if (isSubmitted) {
      return {
        disabled: true,
        text: "Diagnosis Submitted",
        icon: null
      };
    }

    if (!diagnosticReadiness.canSubmitDiagnosis) {
      return {
        disabled: true,
        text: "Cannot Submit",
        icon: null
      };
    }

    if (!diagnosisInput.trim()) {
      return {
        disabled: true,
        text: "Submit Diagnosis",
        icon: null
      };
    }

    return {
      disabled: false,
      text: "Submit Diagnosis",
      icon: null
    };
  };

  const buttonState = getButtonState();

  return (
    <>
      <Box
        sx={{
          p: fullHeight ? 0 : 2,
          height: fullHeight ? "100%" : "50%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: fullHeight ? "transparent" : "background.paper",
          borderBottomLeftRadius: fullHeight ? 0 : "10px",
          borderBottomRightRadius: fullHeight ? 0 : "10px",
        }}
      >
        {/* Header */}
        {!fullHeight && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
            }}
          >
            <FileText size={20} color="#3399a3" />
            <Typography
              sx={{
                fontWeight: 500,
                color: "#3399a3",
              }}
            >
              Submit Diagnosis
            </Typography>
          </Box>
        )}

        {/* Notes Area */}
        <Box sx={{ flex: 1, mb: 2 }}>
          <TextField
            multiline
            fullWidth
            minRows={fullHeight ? 4 : undefined}
            value={diagnosisInput}
            onChange={(e) => setDiagnosisInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Record your diagnostic observations and conclusions..."
            disabled={isSubmitted}
            sx={{
              height: "100%",
              "& .MuiInputBase-root": {
                height: "100%",
                alignItems: "flex-start",
                backgroundColor: isSubmitted ? "rgba(0,0,0,0.05)" : "background.paper",
                color: "text.primary",
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: (theme) => theme.palette.divider,
                },
                "&:hover fieldset": {
                  borderColor: (theme) => isSubmitted ? theme.palette.divider : theme.palette.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: (theme) => theme.palette.primary.main,
                },
              },
              "& .MuiInputBase-input": {
                height: "100% !important",
                fontWeight: 400,
                "&::placeholder": {
                  color: "text.primary",
                  opacity: isSubmitted ? 0.5 : 1,
                },
              },
            }}
          />
        </Box>

        {/* Submit Button */}
        <Button
          variant="contained"
          onClick={handleSubmitDiagnosis}
          fullWidth
          disabled={buttonState.disabled}
          sx={{
            bgcolor: "#3399a3",
            color: "white",
            fontWeight: 500,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#2e8b95",
              boxShadow: "none",
            },
            "&:disabled": {
              bgcolor: "#fafafa",
              color: "#00000042",
            },
          }}
        >
          {buttonState.icon}
          {buttonState.text}
        </Button>
      </Box>
    </>
  );
};
